<template>
  <div class="legal-page">
    <PortalHeader />
    <main class="legal-main">
      <div class="legal-hero">
        <div class="container legal-hero-inner">
          <nav class="legal-crumb" aria-label="面包屑">
            <router-link to="/">首页</router-link>
            <i class="bi bi-chevron-right"></i>
            <span>{{ crumbLabel || title }}</span>
          </nav>
          <div class="legal-hero-row">
            <div class="legal-hero-text">
              <span class="legal-badge">法律文档</span>
              <h2 class="legal-title">{{ title }}</h2>
              <p v-if="meta" class="legal-meta">{{ meta }}</p>
            </div>
            <router-link to="/" class="legal-back">
              <i class="bi bi-arrow-left"></i>
              返回首页
            </router-link>
          </div>
          <div v-if="$slots.intro" class="legal-intro">
            <slot name="intro" />
          </div>
        </div>
      </div>

      <div class="container legal-body">
        <aside v-if="navItems.length" class="legal-aside">
          <div class="aside-card">
            <div class="aside-title">目录</div>
            <nav class="aside-nav">
              <a
                v-for="item in navItems"
                :key="item.id"
                :href="'#' + item.id"
                class="aside-link"
                @click.prevent="scrollToSection(item.id)"
              >
                {{ item.label }}
              </a>
            </nav>
          </div>
        </aside>

        <article class="legal-article">
          <slot />
        </article>
      </div>
    </main>
    <PortalFooter />
  </div>
</template>

<script>
import PortalHeader from '@/components/portal/PortalHeader.vue'
import PortalFooter from '@/components/portal/PortalFooter.vue'

export default {
  name: 'LegalDocShell',
  components: { PortalHeader, PortalFooter },
  props: {
    title: {
      type: String,
      required: true
    },
    crumbLabel: {
      type: String,
      default: ''
    },
    meta: {
      type: String,
      default: ''
    },
    navItems: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    scrollToSection(id) {
      const el = document.getElementById(id)
      if (!el) {
        return
      }
      const top = el.getBoundingClientRect().top + window.pageYOffset - 88
      window.scrollTo({ top, behavior: 'smooth' })
    }
  }
}
</script>

<style scoped>
.legal-page {
  min-height: 100vh;
  background: #f3f4f8;
}

.legal-main {
  padding-top: var(--header-height, 64px);
}

.legal-hero {
  background: #fff;
  border-bottom: 1px solid #e8ebf2;
}

.legal-hero-inner {
  padding: 28px 24px 32px;
}

.legal-crumb {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  font-size: 13px;
  color: var(--text-muted, #999);
}

.legal-crumb a {
  color: var(--text-secondary, #666);
  transition: color 0.2s ease;
}

.legal-crumb a:hover {
  color: var(--primary, #1976d2);
}

.legal-crumb i {
  font-size: 11px;
}

.legal-crumb span:last-child {
  color: var(--primary, #1976d2);
}

.legal-hero-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
}

.legal-badge {
  display: inline-block;
  margin-bottom: 10px;
  padding: 4px 12px;
  border-radius: 999px;
  background: var(--primary-light, #e3f2fd);
  color: var(--primary, #1976d2);
  font-size: 12px;
  font-weight: 600;
}

.legal-title {
  margin: 0;
  font-size: clamp(30px, 3.5vw, 40px);
  font-weight: 700;
  line-height: 1.2;
  color: var(--text-primary, #333);
}

.legal-meta {
  margin: 10px 0 0;
  font-size: 14px;
  color: var(--text-muted, #999);
}

.legal-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 40px;
  padding: 0 18px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: var(--text-secondary, #666);
  font-size: 14px;
  transition: all 0.2s ease;
}

.legal-back:hover {
  border-color: var(--primary, #1976d2);
  color: var(--primary, #1976d2);
  background: #fafaff;
}

.legal-intro {
  margin-top: 22px;
  padding: 18px 22px;
  border-radius: 14px;
  background: linear-gradient(135deg, #f3f8ff 0%, #f3f6ff 100%);
  border: 1px solid rgba(25, 118, 210, 0.12);
  font-size: 15px;
  line-height: 1.8;
  color: var(--text-secondary, #666);
}

.legal-body {
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 28px;
  align-items: start;
  padding: 32px 24px 64px;
}

.legal-aside {
  position: sticky;
  top: calc(var(--header-height, 64px) + 20px);
}

.aside-card {
  padding: 20px 18px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #e8ebf2;
  box-shadow: 0 8px 30px rgba(15, 23, 42, 0.04);
}

.aside-title {
  margin-bottom: 14px;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary, #333);
}

.aside-nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: calc(100vh - 180px);
  overflow-y: auto;
}

.aside-link {
  display: block;
  padding: 9px 12px;
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-secondary, #666);
  transition: background 0.2s ease, color 0.2s ease;
}

.aside-link:hover {
  background: var(--primary-light, #e3f2fd);
  color: var(--primary, #1976d2);
}

.legal-article {
  padding: 36px 44px 44px;
  border-radius: 20px;
  background: #fff;
  border: 1px solid #e8ebf2;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.05);
}

.legal-article >>> .legal-block {
  scroll-margin-top: 88px;
  padding-bottom: 32px;
  margin-bottom: 32px;
  border-bottom: 1px solid #f0f2f6;
}

.legal-article >>> .legal-block:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.legal-article >>> .legal-block h2 {
  margin: 0 0 18px;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary, #333);
}

.legal-article >>> .legal-block p {
  margin: 0 0 14px;
  font-size: 15px;
  line-height: 1.9;
  color: #4b5563;
}

.legal-article >>> .legal-block p:last-child {
  margin-bottom: 0;
}

.legal-article >>> .legal-block ul {
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 10px;
}

.legal-article >>> .legal-block li {
  position: relative;
  padding: 12px 14px 12px 36px;
  border-radius: 12px;
  background: #f9fafb;
  font-size: 15px;
  line-height: 1.75;
  color: #4b5563;
}

.legal-article >>> .legal-block li::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 1.15em;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--primary, #1976d2);
}

.legal-article >>> .legal-block strong {
  color: #374151;
  font-weight: 600;
}

@media (max-width: 1024px) {
  .legal-body {
    grid-template-columns: 1fr;
    gap: 20px;
    padding-top: 24px;
  }

  .legal-aside {
    position: static;
  }

  .aside-nav {
    flex-direction: row;
    flex-wrap: wrap;
    max-height: none;
    gap: 8px;
  }

  .aside-link {
    padding: 8px 14px;
    border: 1px solid #e8ebf2;
    background: #fafafa;
  }
}

@media (max-width: 768px) {
  .legal-hero-inner {
    padding: 22px 16px 26px;
  }

  .legal-hero-row {
    flex-direction: column;
  }

  .legal-back {
    width: 100%;
    justify-content: center;
  }

  .legal-body {
    padding: 20px 16px 48px;
  }

  .legal-article {
    padding: 24px 20px 28px;
    border-radius: 16px;
  }

  .legal-article >>> .legal-block {
    padding-bottom: 24px;
    margin-bottom: 24px;
  }
}
</style>
